// Cookie consent management utilities for GDPR/CCPA compliance

export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

export interface CookieCategoryInfo {
  id: CookieCategory;
  name: string;
  description: string;
  required: boolean;
  cookies: string[];
}

export const COOKIE_CATEGORIES: CookieCategoryInfo[] = [
  {
    id: 'necessary',
    name: 'Necessary',
    description: 'Essential cookies required for the website to function properly. These cannot be disabled.',
    required: true,
    cookies: ['session', 'csrf_token', 'cookie_consent'],
  },
  {
    id: 'functional',
    name: 'Functional',
    description: 'Cookies that enhance functionality like remembering preferences and providing personalized features.',
    required: false,
    cookies: ['theme', 'language', 'checkout_progress'],
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Cookies that help us understand how visitors interact with our website to improve user experience.',
    required: false,
    cookies: ['_ga', '_gid', '_gat', 'sentry_session'],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Cookies used to deliver relevant advertisements and track ad campaign performance.',
    required: false,
    cookies: ['_fbp', '_fbc', 'fbclid'],
  },
];

const CONSENT_COOKIE_NAME = 'btw_cookie_consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365;

// Default preferences - only necessary cookies enabled
export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: 0,
  version: CONSENT_VERSION,
};

// All cookies accepted
export const ALL_ACCEPTED_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
  timestamp: 0,
  version: CONSENT_VERSION,
};

/**
 * Get stored cookie preferences from localStorage
 */
export function getStoredPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (!stored) return null;

    const preferences = JSON.parse(stored) as CookiePreferences;

    // Check if version matches - if not, preferences need to be re-collected
    if (preferences.version !== CONSENT_VERSION) {
      return null;
    }

    // Ensure necessary is always true
    preferences.necessary = true;

    return preferences;
  } catch {
    return null;
  }
}

/**
 * Save cookie preferences to localStorage
 */
export function savePreferences(preferences: CookiePreferences): void {
  const prefsToSave: CookiePreferences = {
    ...preferences,
    necessary: true, // Always ensure necessary is true
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };

  try {
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(prefsToSave));

    // Also set a regular cookie for server-side detection
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    document.cookie = `${CONSENT_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('Failed to save cookie preferences:', error);
  }
}

/**
 * Clear all non-necessary cookies
 */
export function clearNonNecessaryCookies(): void {
  const necessaryCookies = COOKIE_CATEGORIES
    .find(cat => cat.id === 'necessary')?.cookies || [];

  // Get all cookies and remove those not in necessary category
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim();
    if (!necessaryCookies.includes(cookieName) && cookieName !== CONSENT_COOKIE_NAME) {
      // Remove cookie by setting expiry in the past
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    }
  }
}

/**
 * Check if a specific cookie category is allowed
 */
export function isCategoryAllowed(category: CookieCategory): boolean {
  const preferences = getStoredPreferences();
  if (!preferences) return category === 'necessary';
  return preferences[category];
}

/**
 * Check if consent has been given (any choice made)
 */
export function hasConsentBeenGiven(): boolean {
  return getStoredPreferences() !== null;
}

/**
 * Initialize analytics scripts based on consent
 * This should be called after consent is given or on page load if consent exists
 */
export function initializeAnalytics(preferences: CookiePreferences): void {
  if (preferences.analytics) {
    initGoogleAnalytics();
  }

  if (preferences.marketing) {
    initFacebookPixel();
  }
}

/**
 * Initialize Google Analytics 4 (if consent given)
 * Supports both VITE_GA4_MEASUREMENT_ID and VITE_GA_MEASUREMENT_ID for compatibility
 * Includes enhanced e-commerce tracking configuration
 */
function initGoogleAnalytics(): void {
  const gaId = import.meta.env.VITE_GA4_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!gaId) return;

  // Check if already loaded - update consent if so
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      personalization_storage: 'granted',
    });
    console.log('[Analytics] GA4 consent updated to granted');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  // Set consent mode defaults for GA4
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied', // Will be updated separately for marketing consent
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    personalization_storage: 'granted',
    security_storage: 'granted',
  });

  // Initialize GA4 with enhanced e-commerce configuration
  window.gtag('js', new Date());
  window.gtag('config', gaId, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
    // Custom dimensions for Life Itself Studio e-commerce tracking
    custom_map: {
      dimension1: 'biome_category',
      dimension2: 'customer_segment',
      dimension3: 'checkout_step',
      dimension4: 'has_insurance',
      dimension5: 'is_gift_order',
    },
  });

  // Load GA4 script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  script.async = true;
  document.head.appendChild(script);

  console.log(`[Analytics] Google Analytics 4 initialized with ID: ${gaId}`);
}

/**
 * Initialize Facebook Pixel (if consent given)
 *
 * Features:
 * - Event deduplication support via event_id
 * - Automatic advanced matching for improved attribution
 * - Consent mode integration for GDPR/CCPA compliance
 * - Dynamic product ads (DPA) catalog integration support
 *
 * Works in conjunction with @/lib/facebook-pixel.ts for event tracking
 * and server-side Conversions API for iOS privacy compliance.
 */
function initFacebookPixel(): void {
  const pixelId = import.meta.env.VITE_FB_PIXEL_ID;
  if (!pixelId) return;

  // Check if already loaded - just update consent if so
  if (typeof window.fbq === 'function' && window.fbq.loaded) {
    window.fbq('consent', 'grant');
    console.log('[Analytics] Facebook Pixel consent granted (already loaded)');
    return;
  }

  // Facebook Pixel initialization with queue support
  const n: FacebookPixel = Object.assign(
    function(...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod.apply(n, args);
      } else {
        n.queue.push(args);
      }
    },
    {
      push: function(...args: unknown[]) {
        if (n.callMethod) {
          n.callMethod.apply(n, args);
        } else {
          n.queue.push(args);
        }
      },
      loaded: true,
      version: '2.0',
      queue: [] as unknown[][],
      callMethod: undefined as ((...args: unknown[]) => void) | undefined,
    }
  );

  window.fbq = n;
  window._fbq = n;

  // Load Facebook Pixel script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  // Initialize pixel with automatic advanced matching enabled
  // Advanced matching data will be set later via setAdvancedMatching()
  // when user provides email/phone during checkout
  window.fbq('init', pixelId, {}, {
    autoConfig: true,     // Auto-configure button click and page metadata
    debug: import.meta.env.DEV, // Enable debug mode in development
  });

  // Grant consent since marketing cookies are allowed
  window.fbq('consent', 'grant');

  // Track initial PageView
  window.fbq('track', 'PageView');

  console.log(`[Analytics] Facebook Pixel initialized with ID: ${pixelId}`);
}

/**
 * Disable analytics tracking
 * Updates GA4 consent mode to denied and revokes Facebook Pixel consent
 */
export function disableAnalytics(): void {
  // Disable Google Analytics 4 using consent mode
  const gaId = import.meta.env.VITE_GA4_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (gaId && window.gtag) {
    // Update consent to denied
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      personalization_storage: 'denied',
    });
    // Also disable page views
    window.gtag('config', gaId, { send_page_view: false });
    // Set opt-out cookie
    document.cookie = `ga-disable-${gaId}=true; expires=Thu, 31 Dec 2099 23:59:59 UTC; path=/`;
  }

  // Disable Facebook Pixel
  if (window.fbq) {
    window.fbq('consent', 'revoke');
  }

  console.log('[Analytics] Analytics tracking disabled');
}

/**
 * Update marketing consent (for ad tracking)
 */
export function updateMarketingConsent(enabled: boolean): void {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: enabled ? 'granted' : 'denied',
      ad_user_data: enabled ? 'granted' : 'denied',
      ad_personalization: enabled ? 'granted' : 'denied',
    });
    console.log(`[Analytics] Marketing consent ${enabled ? 'granted' : 'denied'}`);
  }
}

// Type declarations for analytics libraries
interface FacebookPixel {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: FacebookPixel;
    _fbq: FacebookPixel;
  }
}
